// Every tunable number the simulation reads, in one object.
//
// Gathered here 2026-07-31 (Phase 1) from two places: the top of `constants.ts`,
// and the magnitude ladders that were written inline in the cast handlers as
// `event.scale === 1 ? 1 : event.scale === 10 ? 4 : 8`. Both were fine while the
// only way to change a number was to edit a source file and reload; neither
// works for the point of the exercise, which is asking "does this puzzle become
// solvable if regen is faster?" without a rebuild.
//
// Two rules this file exists to enforce:
//
// **The sim reads the config, never the constants.** `world.physics` is what
// every rule below consults, so an editor that hands a world a tuned config is
// tuning the real simulation rather than a parallel copy of its numbers. The
// names still exported from `constants.ts` are aliases for the *defaults* --
// convenient for tests that want to say "one regen tick's worth" -- and nothing
// inside the sim should read them at runtime.
//
// **No parameter for a system that does not exist.** Deliberately absent:
// gravity, momentum damping, friction, combustion thresholds, boiling points,
// water depth, heat-transfer coefficients. None of those are simulated, and a
// dial that moves nothing is worse than no dial -- it implies a model the world
// does not have. Every field here corresponds to a number some rule reads today.

/** Precise / Standard / Unbound, the three magnitudes `magnitudeScale()` reports
 * as 1 / 10 / 100. Every ladder in a cast handler is one of these. */
export interface MagnitudeTable {
  small: number;
  standard: number;
  unbound: number;
}

/** Picks a magnitude table's entry for a resolved cast's scale. Mirrors the
 * ternary chains this replaced exactly, including their final `else`: anything
 * that is not 1 or 10 reads as Unbound. */
export function atScale(table: MagnitudeTable, scale: number): number {
  return scale === 1 ? table.small : scale === 10 ? table.standard : table.unbound;
}

// ---------------------------------------------------------------------------
// How a value approaches a target: two shapes, and which mechanism gets which.
//
// Everything in this simulation that drifts toward something else used to do it
// the same way -- close a *fraction of the remaining gap* per tick. That is
// Newton's law of cooling, it is the right physics for two bodies exchanging
// heat, and it has one property that made a whole class of puzzle
// inexpressible: it never arrives. A gap of 5 closing at 0.3 a tick goes 3.5,
// 2.45, 1.715, ... forever. So the world could not say "the ice melts in four
// moves", or "this network holds its charge for three", which is exactly what
// `07_Open_Questions.md` asks for and what an authored timing puzzle is made of.
//
// As of 2026-08-12 there are two shapes, and which one a mechanism uses is a
// claim about what that mechanism is *for*:
//
// **Counted** (`*PerTick`, via `countedDelta`) -- a fixed number of units per
// tick, stopping exactly on the target. Used where the point is that the player
// can count it: ambient decay, a state ladder's pull toward its own
// equilibrium, a reservoir refilling toward `maxProperties`, and a charge
// network relaxing. These arrive, in a knowable number of ticks, and a
// `meltStages` ladder drawn from the value is then a visible countdown.
//
// **Proportional** (`conductance` / `*Rate`, via `proportionalDelta`) -- a
// fraction of the remaining gap, as before, but snapping shut once the
// remainder is negligible so that it terminates. Used where the *magnitude of
// the gap is the mechanic*: standing on something hot, a fluid conducting into
// another fluid, a `Bind`·`Energy` link, and the caster's own thermal inertia.
// Converting these would have been actively wrong. Lava (`energy` 30) and a
// Scorched Floor (10) carry the identical `thermalContact` cluster at the
// identical conductance and differ *only* in how hot they are -- "severity is a
// consequence rather than a constant", as `objectCatalog.ts` puts it. A fixed
// step per tick does not care how hot anything is, so it would have made the
// two equally dangerous and deleted the design. The same argument covers a
// lava channel meeting a water one, and a `Bind` to something cold being a real
// counter to a big gap rather than a token one.
//
// The two live side by side in one solver (`sim/thermal.ts`'s `ThermalEdge`
// carries whichever it is), which is why they are defined together here rather
// than inline at their call sites.
// ---------------------------------------------------------------------------

/** Below this, a remaining gap is treated as closed rather than chased.
 *
 * Small enough to be invisible against every threshold in the game (the
 * tightest margin anything is authored to is 0.5), large enough that a
 * proportional approach reaches it in a bounded number of ticks instead of
 * asymptoting forever. This is what makes the proportional shape *terminate*:
 * binding a 6 and a 0 now genuinely reaches 3 and satisfies a `>= 3` gate,
 * where before it approached 3 from below and never did. */
export const ARRIVAL_EPSILON = 1e-6;

/** A counted step: `perTick` units toward the target, never past it.
 *
 * `gap` is `target - current`; the result is the signed amount to move. Sign is
 * taken from the gap rather than assumed, because every mechanism using this
 * runs in both directions -- decay pulls an object down toward ambient and
 * regen pulls a reservoir up toward its ceiling, and a charge network relaxes
 * whichever way it is currently off. */
export function countedDelta(gap: number, perTick: number): number {
  return Math.sign(gap) * Math.min(Math.abs(gap), perTick);
}

/** A proportional step: `fraction` of the remaining gap, snapping the last
 * sliver shut so the approach ends. See `ARRIVAL_EPSILON`. */
export function proportionalDelta(gap: number, fraction: number): number {
  return Math.abs(gap) <= ARRIVAL_EPSILON ? gap : gap * fraction;
}

import { FluidId, MobVariantId } from './types';

export interface PhysicsConfig {
  /** What makes time pass in this world. */
  clock: {
    /** How much simulated time one player action is worth, in milliseconds --
     * or **0 for the wall clock**, where time comes from however long the frame
     * took and passes whether or not the player did anything.
     *
     * One number rather than a `'wall' | 'turn'` enum beside it, deliberately.
     * Two fields can contradict each other (`mode: 'wall'` with a meaningful
     * `msPerTurn`, or the reverse) and something would have to decide which
     * wins; one cannot. It also keeps every dial in this file numeric, which is
     * what lets the editor's tuning panel hold this without growing a second
     * kind of setter (see `tunePhysics`). Read it through `isTurnClock` rather
     * than comparing to 0 at each site.
     *
     * **The sim itself barely cares.** `tick(world, dtMs)` has taken elapsed
     * time as an argument since Phase 0 and still does; nothing in `sim/` reads
     * a clock. What this number decides is who calls it and with what -- the
     * game's frame loop with a frame delta, or an action handler with exactly
     * this. Every interval below (`manaRegenTickMs`, `decayTickMs`, the mob
     * cadences) keeps working unchanged either way.
     *
     * **The timing this makes countable is real**, as of the mechanics change
     * later the same day: ambient decay and charge relaxation move a counted
     * number of units per tick and stop on arrival (see `countedDelta` above),
     * and every interval in this file divides evenly into whole turns, which
     * `sim.test.ts` pins. So "the ice melts in four moves" is expressible, and
     * `turnsPer` is the conversion.
     *
     * Defaults to 0: the wall clock is what the game shipped on and what every
     * existing recording, save and test was made against. A recording made
     * before this field existed inherits the 0 automatically, because
     * `worldFromRecording` spreads the defaults *under* the recording's own
     * config -- so an old take still replays on the clock it was taken on. */
    msPerTurn: number;
  };
  /** The caster's own body: what it can hold, and how fast it recovers. */
  player: {
    maxMana: number;
    maxHealth: number;
    maxCharge: number;
    /** A passive trickle -- enough that one costly cast isn't permanently
     * crippling, slow enough that repeated big draws still feel risky. Full
     * 0->10 recovery takes 80s (2026-07-28: sped up from 200s -- emptying your
     * reserve by accident meant several real minutes of just waiting, which
     * read as a bug, not a cost). The tick *interval* was deliberately left
     * alone when that was tuned (bumping the per-tick amount instead of
     * shortening the interval): shortening it first made two tests with exact
     * HUD-bar percentage assertions (momentumProject.spec.ts, puzzle.spec.ts's
     * over-ambitious Unbound self-cast) start intermittently failing, because
     * their own real-world duration is close enough to the shorter interval
     * that a tick could land mid-sequence. A less frequent, bigger tick hits
     * the same overall speed without that risk. */
    manaRegenPerTick: number;
    manaRegenTickMs: number;
    /** The nominal top of the caster's own body-heat scale -- see
     * PlayerState.thermalState. **Not a cap** (2026-08-09): a temperature has
     * no maximum, and the clamp this used to impose meant that being at the
     * top of the scale made the caster *immune* to further heating, which is
     * exactly backwards. What it sizes now is the HUD bar and the reading a
     * "full" caster is drawn against; thermalState itself runs past it freely,
     * and `checkThermalDanger` bills for every point of it. Only the floor at
     * 0 is real. */
    maxThermalState: number;
    /** The neutral "Comfortable" resting point a fresh caster (and a room
     * restart after death) starts at -- unlike every other reserve, this one
     * doesn't start full or empty, since both ends are dangerous. */
    thermalStart: number;
    /** At or below this, the caster reads as dangerously cold ("Frozen") and
     * takes damage every time it's checked (see checkThermalDanger). Set
     * symmetrically around thermalStart, each threshold exactly one Standard
     * cast's amount (transfer.amount.standard) away from it -- the same
     * "a Standard cast crosses a threshold outright" convention every other
     * concept's own thresholds already follow (metal.rustEntropy,
     * sand.meltEnergy, vines.burningEnergy). */
    thermalFrozenThreshold: number;
    /** At or above this, the caster reads as dangerously hot ("Burning"). */
    thermalBurningThreshold: number;
    /** Health lost per danger check, **per point outside the survivable
     * band** -- so the bill ramps from nothing exactly at either threshold and
     * grows with how far past it the caster has gone. Small and repeated, so
     * sustained exposure is an escalating threat rather than an instant punish -- and now genuinely
     * escalating in two ways at once, since standing somewhere hotter both
     * raises the reading faster and bills more for it. Replaced a flat
     * per-check amount on 2026-08-09; see checkThermalDanger for why damage is
     * a function of state rather than of heat flux.
     *
     * **What "repeated" means, exactly** (corrected 2026-08-12): once per
     * room-decay tick, via `solveThermalStep`, plus once per cast that moves the
     * caster's own thermalState, via `drawThermal`/`giveThermal`. It is *not*
     * per settle-triggering mutation, which this comment claimed and which
     * `review/2026-08-07-architecture-review.md` §4.10 then repeated back as a
     * design concern -- that walking back and forth would take damage faster
     * than standing still. Walking settles and does not touch a thermal locus,
     * so it does not. The damage from standing somewhere dangerous is
     * time-based, which is what it should be; the per-cast check is a separate
     * and deliberate thing, billing a cast that pushes the caster into a band. */
    thermalDamagePerExcess: number;
    /** How fast the caster's own thermalState closes the gap toward the
     * current room's ambientEnergy per room-decay tick -- deliberately its
     * own, much slower rate than `room.decayPerTick` (a person's body has far
     * more thermal inertia than a small block of ice or an ambient-sensitive
     * game object). **Deliberately still proportional** while `room.decayPerTick`
     * became counted (2026-08-12): the caster equilibrating with the room they
     * stand in is the same two-bodies-exchanging-heat shape as standing on
     * something hot, not a countdown anything is authored against -- and
     * room13's Scorched Floor gauntlet balances this edge against a
     * `thermalContact` one to settle the caster at a measured 9.08, which a
     * counted version would move. See the two shapes at the top of this file.
     * Needed because
     * `room.decayPerTick` was tuned only against object barriers, and almost
     * every shipped room's `ambientEnergy` (authored purely for that
     * purpose, before thermalState existed) sits at or below
     * `thermalFrozenThreshold` -- at `room.decayPerTick`'s speed, simply
     * standing in nearly any ordinary room starts costing Health within
     * ~15s, which was never the intent (only the Ice Vault was meant to
     * read as dangerously cold). This is a stopgap, not the real fix --
     * see `09_Roadmap.md` for the planned pass re-authoring room
     * `ambientEnergy` (and probably the object thresholds pinned to the
     * same numbers) so ordinary rooms sit near `thermalStart` and only
     * deliberately extreme rooms are actually hazardous; this rate exists
     * so idling in an ordinary room stays safe in the meantime. */
    thermalDecayRate: number;
  };
  /** Environmental Energy, and how quickly a room reclaims it. */
  room: {
    /** **Counted** (see `countedDelta`): each tick, every object's Energy moves
     * this many units toward its room's ambientEnergy, stopping exactly there.
     * The same number drives a `heatState` ladder's pull toward its own
     * equilibrium and a reservoir's refill toward `maxProperties` -- one rule for
     * "the room reclaims its own baseline", in both directions.
     *
     * **1.5, and the number is doing a specific job.** It is deliberately larger
     * than a Precise transfer (1) and smaller than a Standard one (4), which is
     * what preserves the rule this dial has always existed for: a single
     * big-enough cast crosses a `meltsWhen` threshold in one atomic resolution,
     * but Energy trickled in a point at a time bleeds back out between attempts
     * instead of accumulating. Counting makes that rule *sharper* than the old
     * fraction-of-the-gap did, where whether a trickle survived depended on how
     * far from ambient the object happened to be.
     *
     * It also makes the span countable, which is the point of the whole shape:
     * an object 5 above ambient is back at ambient in 4 ticks, and one drained
     * to 0 with a ceiling of 10 is full again in 7 -- numbers a puzzle can be
     * built on and a player can learn. Was `decayRate: 0.3` of the remaining gap
     * until 2026-08-12. */
    decayPerTick: number;
    decayTickMs: number;
  };
  /** One threshold pair per fluid (see `FluidId`). Water and lava run the same
   * state machine -- liquid above the melting point, solid below the freezing
   * point, hysteresis between -- and differ only in where those points sit, so
   * this is a table rather than two hand-written blocks. A third fluid needs a
   * row here and nothing else. */
  fluid: Record<FluidId, {
    /** A flowing channel tile whose own properties.energy drops to or below
     * this freezes (see checkFluidFreeze, heatState.ts) and stops carrying its
     * fluid until it melts again. Water becomes ice; lava becomes volcanic
     * rock. */
    freezeThreshold: number;
    /** A frozen channel tile whose own properties.energy rises to or above
     * this melts back to liquid. Deliberately a different, higher number than
     * freezeThreshold -- real two-threshold hysteresis, so an already-frozen
     * tile doesn't immediately re-thaw from the same value that could have
     * frozen a liquid one. */
    meltThreshold: number;
    /** **Proportional, deliberately** (see the two shapes at the top of this
     * file): how much of the gap between two orthogonally adjacent *flowing*
     * tiles of fluid closes per tick -- a fluid carries heat along itself, and to
     * whatever other fluid it touches. At an interface between two kinds the
     * two conductances average, the way a series path through two materials
     * is governed by both.
     *
     * **Only flowing tiles conduct**, which is a physical statement and not a
     * convenience: the flow is what carries the heat, so a frozen tile is out
     * of the circuit at both ends. That it also leaves room9's freeze puzzle
     * exactly as it was -- a frozen channel tile has no warm neighbour pulling
     * it back over its own melting point -- is a consequence of the rule
     * rather than an exception carved for it. */
    conductance: number;
  }>;
  charge: {
    /** **Counted** (see `countedDelta`): how many units of Charge a network moves
     * toward its equilibrium -- the sum of its sources' rated output
     * (`maxProperties.charge`) -- per room-decay tick, stopping exactly there.
     * The same shape `room.decayPerTick` uses for Energy.
     *
     * **Both directions**, which is the whole reason this exists. Regen used to
     * pull a source back up toward its own maximum and nothing pulled injected
     * Charge back down, so a network could be topped up and banked
     * indefinitely -- the pump the design review logs in Phase 7. A restoring
     * force that only acts on one side is not an equilibrium.
     *
     * **Only networks that contain a source relax at all.** With no generator
     * there is nothing to restore anything, and an isolated charged conductor
     * really does just stay charged; making sourceless networks decay would be
     * charge quietly evaporating, which is a different (and unphysical) claim.
     * That is also what keeps room8's hand-charged Standing Pillars working:
     * they sit on no source's network, so nothing pulls them anywhere.
     *
     * Set to `room.decayPerTick`'s own 1.5 so this stays the pure symmetry it was
     * introduced as -- Charge relaxes exactly as fast as Energy reclaims its
     * baseline. Its own dial rather than a reuse of `room.decayPerTick`, per this
     * file's standing rule that two rules sharing a value stay separate fields
     * so tuning one cannot silently retune the other.
     *
     * **This is the dial `07_Open_Questions.md` asked to be countable, and now
     * is.** It wanted "this network holds its charge for three moves" -- a number
     * the player can count rather than a fraction of a gap that never arrives.
     * At 1.5, a network topped 4 above its equilibrium (the scale every conduit
     * threshold in the world is authored at) is back to equilibrium in exactly 3
     * ticks. Under the turn clock, that is three moves. Was `relaxRate: 0.3` of
     * the remaining gap until 2026-08-12. */
    relaxPerTick: number;
  };
  /** How much of a concept one Transfer moves between its two loci. */
  transfer: {
    amount: MagnitudeTable;
  };
  /** Transfer·Momentum: a shove on one adjacent object, and the caster's recoil.
   * Tiles and cost are separate ladders because they are separate questions --
   * "how far does a bigger push carry" and "what does a bigger push cost". */
  momentum: {
    tiles: MagnitudeTable;
    energyCost: MagnitudeTable;
  };
  /** Project: a quantity (or a force) pushed outward from the caster. */
  project: {
    /** Delivered to everything in range in total, not per target -- the beam
     * attenuates as it penetrates (see applyProjectEffect). Also the reserve
     * the cast has to be able to cover before it fires at all. */
    amount: MagnitudeTable;
    /** How far the projection reaches, shared with radialMeasure's own
     * visualization so a modifier means the same range everywhere it appears. */
    range: MagnitudeTable;
    /** Charge's Radial specifically: a directed discharge (Linear) reaches
     * further than a diffuse field can hold together, so Radial·Charge gets its
     * own, much shorter ladder. */
    chargeRadialRange: MagnitudeTable;
    /** Project·Momentum's push/pull distance -- the same ladder
     * Transfer·Momentum's single shove uses, kept separate so tuning the
     * ranged force field doesn't silently retune the adjacent shove. */
    momentumTiles: MagnitudeTable;
  };
  /** Living vines (see `sim/vines.ts`): where the entropy/energy ladder's
   * breakpoints sit, and how fast sustained burning turns into permanent
   * damage. Every threshold here is a lower bound -- `energy >= growingEnergy`
   * rather than an exact match -- the same "above a threshold" shape every
   * other meltsWhen rule in the game already uses. */
  vines: {
    /** Energy at/above which withered vines read as healthy, and become
     * climbable. */
    healthyEnergy: number;
    /** Energy at/above which vines attempt to grow a new segment into the
     * tile above (see `attemptVineGrowth`) -- still climbable. */
    growingEnergy: number;
    /** Energy at/above which vines are on fire -- not climbable, and each
     * decay tick spent at or above this adds `burnEntropyPerTick` to the
     * vines' own Entropy. */
    burningEnergy: number;
    /** Entropy at/above which vines read as dead, overriding whatever Energy
     * says -- reducible by an ordinary Transfer, the same way room6's
     * Reliquary pair already moves Entropy down off one object. */
    deadEntropy: number;
    /** Entropy added per decay tick while burning. Tuned against the shipped
     * magnitude ladder above, not picked in isolation: a Standard Transfer/
     * Project (4, exactly the burning threshold) singes for one tick and
     * survives (entropy 0.6, cools out of the burning band before a second
     * tick); an Unbound cast (8) stays in the burning band two ticks running
     * and crosses 1.0 -- dead -- on the second. Sustained or repeated
     * overheating kills; a single ordinary overshoot damages but survives. */
    burnEntropyPerTick: number;
  };
  /** Bind (see `sim/thermal.ts`): how fast a linked pair's Energy -- or, for
   * `Bind`·`Charge`, its Charge -- closes the gap toward each other, and what
   * moving that Charge costs. */
  bind: {
    /** Newton's law of cooling again, same shape `room.decayRate` already
     * has: each settle, a bound object's Energy (and its linked partner's)
     * closes this fraction of the gap between them. Runs every settle, not
     * on a timer -- deliberately faster than `room.decayRate` would read in
     * practice, since settle only fires on a real mutation (a cast, a push,
     * a step), never on the room-decay tick a passive reservoir regenerates
     * on -- see EnergyBindDef's own doc comment for why that's what keeps
     * regen from feeding it. */
    energyEquilibrationRate: number;
    // No chargeEquilibrationRate. There was one, 2026-08-07 to 2026-08-08, and
    // removing it is this file's own opening rule applied honestly: a wire
    // equalizes totally and immediately (see chargeBind.ts), so there is no
    // fraction-of-the-gap for anyone to tune, and a dial that moves nothing is
    // worse than no dial because it implies a model the world does not have.
    /** Resistive (Joule) loss: how much the caster's own thermalState rises
     * per unit of Charge that actually crosses a live `Bind`·`Charge` link.
     * The caster is the conductor -- there is nothing else between the two
     * loci -- so the dissipation lands on them. See `ChargeBindDef`. */
    jouleHeatPerCharge: number;
  };
  /** Metal corrosion (see `sim/rust.ts`): a single Entropy threshold, above
   * which a `rust`-carrying object reads as rusted -- new texture, new
   * description, and (if it also carries `conduit.conductive`) no longer
   * conducts. Unlike vines' multi-stage ladder this is one on/off crossing:
   * real corrosion is a slow but genuinely binary state change, not something
   * with a growing middle stage worth modeling separately. */
  metal: {
    rustEntropy: number;
    /** Metal Floor's own hazard threshold (see `queries.isConduitLive`): a
     * connected, still-conductive network of floor tiles reads live the
     * moment any one member's own `properties.charge` reaches this --
     * Charge conducts across the whole reachable network from there, not
     * summed, the same "one source is enough" shape water-flow already has. */
    floorLiveCharge: number;
  };
  /** The relic decay ladder (see `sim/relic.ts`): the two Entropy thresholds
   * separating Pristine from Withered from Fractured, for the whole
   * Reliquary/Standard/Weight family. Two thresholds rather than metal's one
   * because a relic's whole subject is Entropy as a *continuum* -- the family
   * exists to make "entropy moved from here to there" visible -- where metal
   * corrosion is a genuinely binary "does it still conduct". */
  relic: {
    witheredEntropy: number;
    fracturedEntropy: number;
  };
  /** Sand's thermal/entropy phase ladder (see `sim/sand.ts`): Energy at/above
   * `meltEnergy` reads as molten glass regardless of Entropy; cooling back
   * below that lands on glass, at whatever Entropy melting left it with
   * (`glassEntropy`, set on the way in -- melting is a real entropy-
   * increasing phase change, the same physical justification the Candle's
   * own meltEntropy bump already uses); draining Entropy down to
   * `crystalEntropy` turns glass to Quartz; pushing Entropy back up past
   * `shatterEntropy` breaks Quartz back into sand. */
  sand: {
    meltEnergy: number;
    glassEntropy: number;
    crystalEntropy: number;
    shatterEntropy: number;
    /** How fast molten glass exchanges heat with whatever is standing on it --
     * the `thermalContact.conductance` sand.ts derives from the molten stage.
     * Matches the authored floors' own 0.4 (Scorched Floor, Cold Slab, Heated
     * Plate) so a pool of melt underfoot is as fierce as the hottest tile the
     * world already has, and no fiercer. */
    moltenConductance: number;
  };
  /** Light and darkness (see `sim/light.ts`). Visibility only: nothing here
   * changes what the caster may do, only what the renderer is able to show. */
  light: {
    /** How many tiles a source reaches per unit of strength, before the room's
     * own darkness is subtracted.
     *
     * **2, chosen so the whole 1-10 room scale stays usable.** An ordinary
     * correctly-wired lamp sits at network potential 4 (one Charged Font over
     * one unit of capacity, plus the lamp's own), so it reaches `8 - darkness`:
     * 6 tiles in a light-8 room, 4 in a light-6 room, 2 in a light-4 room, its
     * own tile at light 2. At 1 tile per unit the same lamp would light nothing
     * at all below light 6, which would waste half the scale. */
    tilesPerUnit: number;
    /** The furthest any single source reaches, however strong.
     *
     * Without it, Lava (`properties.energy` 30) would light 60 tiles and no room
     * containing any could ever be dark. 8 is the same numeral as
     * `transfer.amount.unbound` -- the most a single cast moves is 8, and the
     * furthest a single source shines is 8 tiles -- which keeps every ceiling in
     * this file pinned to one ladder rather than picked in isolation. */
    maxReach: number;
    /** The strength of an object that is *showing* its `litTexture` while
     * holding neither heat nor charge -- a filled altar pillar.
     *
     * A fixed number because there is nothing live to read: an altar's lit state
     * is a boolean, not a quantity. 4 makes it exactly as bright as an ordinary
     * lamp, which is the honest reading of two things that both look like they
     * are lit. */
    litFixture: number;
    /** How far the caster can see regardless of anything else.
     *
     * Not a flourish: the darkness layer draws above the player sprite, so
     * without this a caster in the dark is invisible rather than in the dark.
     * "You can always see your own feet" is also what keeps a dark room
     * traversable at all rather than a guessing game. Deliberately unaffected by
     * the room's darkness -- it is the one reading that must never fail. */
    casterReach: number;
  };
  /** Conveyors (see `sim/conveyor.ts`). */
  conveyor: {
    /** How often a powered run carries what is standing on it one tile, in
     * milliseconds of simulated time.
     *
     * **1000, so that a belt moves exactly one tile per turn** -- which is what
     * a conveyor has to be for the puzzle game, since a run whose rhythm the
     * player cannot count is a timing race and `16_Puzzle_Charter.md` rules
     * those out. On the wall clock the same number reads as a tile a second,
     * which is a believable pace for a machine and is the cadence a mob is
     * already tuned against (a Grunt steps every two).
     *
     * Its own dial rather than a reuse of `room.decayTickMs`, which today holds
     * the identical value: two rules sharing a number stay separate fields here
     * so that tuning one cannot silently retune the other. A belt's pace and the
     * rate at which a room reclaims its own heat have nothing to do with each
     * other, and the day decay slows down (see that field's own note -- it is
     * expected to) the belts must not slow with it.
     *
     * 0 stops every belt in the world dead, the same "an interval of zero means
     * this rule does not fire" reading `mob.stepMs` and the two ambient rules in
     * `tick` already have. */
    stepMs: number;
  };
  /** One row per kind of mob (see `MobVariantId`, `sim/mob.ts`) -- a table for
   * the same reason `fluid` is one: two kinds of creature are the same rule at
   * different numbers, and a third is a row here rather than a branch in the
   * mechanism. Five kinds share four numbers between them, and every difference
   * anyone has asked for so far -- twice the pace, throws something, keeps its
   * distance -- has been one of these rather than a new rule. */
  mob: Record<MobVariantId, {
    /** How often this kind of mob steps one tile, in milliseconds of simulated
     * time (`tick`'s own argument, so a test skipping four seconds gets exactly
     * two Grunt steps and a paused game gets none).
     *
     * Deliberately much slower than the caster's own step, which is limited only
     * by how fast keys are pressed: a mob that closed distance as fast as the
     * player could open it would make every room a chase, where two seconds is
     * slow enough to be walked around, out-thought, or shoved off course -- and
     * fast enough that ignoring one is a decision rather than an oversight.
     * Provisional in the same way `charge.relaxRate` is: a wall-clock cadence is
     * the wrong unit for a world that may yet move to turns (see
     * `07_Open_Questions.md`), and this is the dial to change if it does. */
    stepMs: number;
    /** How often this kind throws its projection. Absent for a kind that throws
     * nothing (Grunt, Swarmer), which is what "does this creature cast" *is* --
     * there is no separate flag saying so.
     *
     * Its own clock rather than a multiple of `stepMs`, and the numbers are
     * chosen not to divide each other (3500 against 2000): a caster who can
     * hear a mob's rhythm as "one spell every two steps" is counting steps, and
     * a mob that walks and casts on drifting clocks has to be watched instead. */
    castMs?: number;
    /** How far the projection reaches, in tiles, before it stops being drawn.
     * The real cast's own Standard reach is 6 (`project.range`), which is what a
     * mage gets; an arrow flies further than a thrown spell. */
    castRange?: number;
    /** The distance this kind *wants* to be from the caster, in tiles of
     * Manhattan reach. Absent means "as close as possible", which is every kind
     * but the Archer -- and the Archer is the whole reason this is a number
     * rather than a `keepsDistance` flag: what makes it a different creature to
     * fight is exactly how much room it insists on. */
    standoff?: number;
  }>;
}

/** Today's values, exactly. Deep-frozen: a world that wants different numbers
 * gets its own config (see `clonePhysicsConfig`) rather than writing through the
 * one every default-built world shares. Freezing is what makes that a thrown
 * TypeError in strict mode instead of a world quietly retuning every other
 * world in the process. */
export const DEFAULT_PHYSICS: PhysicsConfig = deepFreeze({
  clock: {
    msPerTurn: 0,
  },
  player: {
    maxMana: 10,
    maxHealth: 10,
    maxCharge: 10,
    manaRegenPerTick: 3,
    manaRegenTickMs: 20_000,
    maxThermalState: 10,
    thermalStart: 5,
    thermalFrozenThreshold: 2,
    thermalBurningThreshold: 8,
    // Deliberately the same numeral as the flat per-check amount it replaced,
    // now meaning "per point outside the band" instead of "whenever outside
    // it". Measured against the shipped content rather than reasoned about: a
    // caster standing on room13's Scorched Floor settles at 9.08, not 10 --
    // contact toward the floor's Energy 10 balances against their own decay
    // toward that room's ambient 0 -- so the steady bill is 0.54/tick against
    // the old flat 0.5. Within a few percent where it matters, while the first
    // two ticks of exposure now cost nothing at all (the ramp starts at the
    // threshold), which is the crossing-versus-lingering distinction the
    // contact mechanism exists to draw. Anything hotter is dramatically worse
    // on both counts at once: it reaches the band sooner and bills more once
    // there.
    thermalDamagePerExcess: 0.5,
    thermalDecayRate: 0.04,
  },
  room: {
    // 1.5 units per tick, not 0.3 of the remaining gap (2026-08-12). Sits
    // between a Precise transfer (1) and a Standard one (4), which is what keeps
    // "a trickle bleeds out, a real cast lands" true -- see the field's own
    // comment. Also makes the 0-10 span exactly 7 ticks and an ambient-to-10 gap
    // exactly 4, so a `meltStages` ladder reads as a countdown.
    decayPerTick: 1.5,
    // 1000, so a decay tick is exactly **one turn** (2026-08-12). This is the
    // finest granularity the world can express: with a 3000 ms tick the shortest
    // authored timing was "in three moves", and there was no way to say "next
    // move" at all. Set deliberately, knowing it triples how often decay fires
    // and therefore how fast the world reclaims its baseline in real time --
    // `decayPerTick` is expected to come down to compensate in a tuning pass, and
    // several shipped puzzles are expected to need re-authoring before then.
    decayTickMs: 1000,
  },
  fluid: {
    water: {
      freezeThreshold: 4,
      meltThreshold: 6,
      // Slower than lava's: the number that matters is the ratio, and rock
      // moving heat faster than water is what makes a lava flow the thing
      // that changes its surroundings rather than the other way round.
      conductance: 0.15,
    },
    // Lava's pair sits just under the Energy its own tiles carry (30), so a
    // channel is molten as authored and freezing one is a real, deliberate
    // act rather than something ambient drift can do: draining 30 down to 20
    // is two Unbound Energy casts, or one sustained Bind to something cold.
    // The gap between the two is wider than water's for the same reason it is
    // harder to re-melt rock than ice -- and it means a frozen bridge stays a
    // bridge unless the player genuinely pours heat back into it.
    lava: {
      freezeThreshold: 20,
      meltThreshold: 26,
      conductance: 0.25,
    },
  },
  charge: {
    // Tracks room.decayPerTick, as it has since it was introduced. At 1.5 a
    // network 4 off its equilibrium is back in exactly 3 ticks -- the "holds its
    // charge for three moves" 07_Open_Questions.md asked for.
    relaxPerTick: 1.5,
  },
  transfer: {
    amount: { small: 1, standard: 4, unbound: 8 },
  },
  momentum: {
    tiles: { small: 1, standard: 2, unbound: 3 },
    energyCost: { small: 1, standard: 4, unbound: 8 },
  },
  project: {
    amount: { small: 1, standard: 4, unbound: 8 },
    range: { small: 3, standard: 6, unbound: 10 },
    chargeRadialRange: { small: 1, standard: 2, unbound: 3 },
    momentumTiles: { small: 1, standard: 2, unbound: 3 },
  },
  vines: {
    healthyEnergy: 1,
    growingEnergy: 2,
    burningEnergy: 4,
    deadEntropy: 1,
    burnEntropyPerTick: 0.6,
  },
  bind: {
    // Noticeably faster than room.decayRate (0.3) since it only ever runs
    // from a real mutation, never a timer -- a rate this high still takes
    // several real actions (not one) to visibly close a large gap, which is
    // the intended "watch it happen over the course of play" feel, not an
    // instant snap to average on the very next settle.
    energyEquilibrationRate: 0.2,
    // Deliberately much faster than Energy's 0.2: a wire is not a draught.
    // At 0.5 a link visibly does its job inside three or four decay ticks
    // rather than a dozen, which is what makes "bind it, watch the far gate
    // light" read as a circuit closing rather than as weather.
    // Pinned to the shipped magnitude ladder the same way metal.rustEntropy
    // and vines.burningEnergy are, and then checked against the caster's own
    // two lethal ends: moving one Standard cast's worth of Charge (4) heats
    // the caster by exactly 1.0, so a single full run of a Charged Font
    // (8/8) into an empty locus -- 4 units cross before the pair equalizes
    // -- takes a resting caster from thermalStart 5 to 6, uncomfortable but
    // survivable. Wired to a Font directly, filling an empty reserve costs
    // about the same again; spending that reserve and letting the Font's own
    // regen replace it three times over reaches thermalBurningThreshold 8 and
    // starts costing Health. Cheap enough to use, expensive enough to plan.
    jouleHeatPerCharge: 0.25,
    // There was a `heatPerEntropy` here too, removed 2026-08-16 with the
    // couplings that spent it: a Bind·Entropy link is a displacement now, and a
    // displacement has nothing to bill for -- no gap closes, nothing crosses
    // the caster, and the Transfer it redirects already pays its own way. This
    // file's own opening rule is that a dial which moves nothing is worse than
    // no dial.
  },
  metal: {
    // Aligned to transfer.amount.standard: one ordinary Standard-magnitude
    // Transfer/Project of Entropy rusts a clean block outright, a Precise graze
    // does not, matching how vines' own thresholds are pinned to this same
    // ladder rather than picked in isolation.
    rustEntropy: 4,
    // Same alignment: one ordinary Standard-magnitude Transfer/Project of
    // Charge, onto any tile in a connected floor network, makes the whole
    // network live.
    floorLiveCharge: 4,
  },
  relic: {
    // Pinned to transfer.amount's 1/4/8 ladder like every other threshold
    // here, and to the exact values the authored relics already used: 4 is
    // what every one of their barrier.meltsWhen rules watched, and 8 is what a
    // Fractured Reliquary shipped holding. So one ordinary Standard Transfer
    // moves a relic exactly one stage, and an Unbound one takes a pristine
    // relic all the way to fractured.
    witheredEntropy: 4,
    fracturedEntropy: 8,
  },
  sand: {
    // Every threshold here is pinned to transfer.amount's own 1/4/8 ladder,
    // the same way metal.rustEntropy and vines.burningEnergy already are:
    meltEnergy: 4, // one ordinary Standard Energy Transfer/Project melts it.
    glassEntropy: 4, // melting sets Entropy here -- Standard-aligned, so a
    // single further Standard Entropy drain (4 -> 0) is enough to reach:
    crystalEntropy: 1, // "drained down near enough to nothing" reads as Quartz.
    shatterEntropy: 8, // an Unbound-magnitude Entropy push is what "too much"
    // takes to shatter Quartz back into sand -- twice glassEntropy, not
    // reachable by a single ordinary Standard cast.
    moltenConductance: 0.4, // the same rate every authored thermal floor uses.
  },
  light: {
    // 2 tiles per unit of strength: an ordinary lamp (network potential 4)
    // reaches 8 minus the room's darkness, which keeps every room light from 1
    // to 9 meaning something different. See the field's own comment.
    tilesPerUnit: 2,
    // Pinned to transfer.amount.unbound, like every other ceiling here. Without
    // it a Lava channel lights any room it is in, whatever the room says.
    maxReach: 8,
    // As bright as an ordinary lamp, because both are things that visibly lit up.
    litFixture: 4,
    // You can always see your own feet, and the tiles you could step onto.
    casterReach: 1,
  },
  conveyor: {
    // One tile per turn, which is the whole design of the thing: a machine the
    // player can count is a machine they can plan around, and one they cannot is
    // a race. See the field's own comment for why this is not `decayTickMs`
    // despite currently being the same number.
    stepMs: 1000,
  },
  mob: {
    // Two seconds: slower than the room decay tick it shares a cadence family
    // with (3s), and far slower than a caster who can step as fast as a key
    // repeats. Measured against nothing yet -- this is the first mob in the
    // world -- so it is a starting point to play against rather than a tuned
    // number, which is exactly what the editor's Physics panel is for.
    grunt: { stepMs: 2000 },
    // Exactly half the Grunt's interval and nothing else different. A Swarmer is
    // the argument for this table being a table: "twice as fast" should cost one
    // number, not one creature's worth of code.
    swarmer: { stepMs: 1000 },
    // A Grunt's pace with a spell on top. 3500 deliberately does not divide 2000
    // (see castMs): the two clocks drift past each other, so the rhythm has to be
    // watched rather than counted off its steps. Reach matches the real cast's
    // own Standard `project.range`, since what it throws is a depiction of one.
    'drone-mage': { stepMs: 2000, castMs: 3000, castRange: 6 },
    // Identical numbers to a Drone Mage on purpose: an apprentice differs by
    // *which* spell comes out, which is not a quantity. If it ever earns its own
    // pace, this is where that goes -- but making it different now would be
    // inventing a difference nobody asked for.
    'mage-apprentice': { stepMs: 2000, castMs: 3000, castRange: 6 },
    // Faster to loose than a mage is to cast, further-reaching, and it wants
    // four tiles of room -- one tile beyond a Precise Project's reach (3) and
    // well inside a Standard one's (6), so the cheap answer to an Archer is to
    // close the distance it is trying to keep and the expensive one is not to.
    // All three provisional, and `standoff` is the interesting one to play with.
    archer: { stepMs: 2000, castMs: 2000, castRange: 8, standoff: 4 },
  },
});

/** The default quantum of simulated time for one player action, for a caller
 * switching a world onto the turn clock and having no opinion about how much a
 * turn is worth.
 *
 * 1000 ms, which is not arbitrary: it makes `decayTickMs` (3000) exactly three
 * turns and `manaRegenTickMs` (20_000) exactly twenty, so every interval already
 * in this file lands on a whole number of turns rather than drifting through
 * them. Nothing enforces that -- a tuned world can pick anything -- but a default
 * that leaves the ambient rules firing on fractional turns would make "how many
 * moves does this take" unanswerable for no gain. */
export const DEFAULT_MS_PER_TURN = 1000;

/** Whether this world advances on player actions rather than on elapsed real
 * time. The one place `msPerTurn`'s 0-means-wall-clock encoding is spelled out,
 * so no call site has to know it. */
export function isTurnClock(config: PhysicsConfig): boolean {
  return config.clock.msPerTurn > 0;
}

/** An interval expressed in player actions rather than milliseconds -- what
 * "how many moves does this take" actually resolves to.
 *
 * **Derived, not a second set of dials.** The obvious alternative was a
 * `decayTurns` beside every `decayTickMs`, and it is worse: two fields for one
 * cadence can disagree, and the day they do, which one is the rule? Every
 * interval in this file stays denominated in milliseconds -- the one thing both
 * clocks have -- and a turn is simply a tick of known size, so the turn count
 * falls out by division and cannot drift from the thing it describes.
 *
 * That only works while every interval divides evenly by `msPerTurn`, which is
 * why `DEFAULT_MS_PER_TURN` is 1000 and why every cadence in `DEFAULT_PHYSICS`
 * is a whole number of seconds. `sim.test.ts` pins that: an interval that did
 * not divide would fire on turn 4, then 7, then 11, and a player could not plan
 * around an enemy whose rhythm has no period they can count. Returns a
 * fractional value rather than rounding when it does not divide, so a tuned
 * world says so instead of quietly lying. Infinity on the wall clock, where the
 * question has no answer. */
export function turnsPer(config: PhysicsConfig, intervalMs: number): number {
  return isTurnClock(config) ? intervalMs / config.clock.msPerTurn : Infinity;
}

/** A config from somewhere older, completed with today's defaults.
 *
 * Two callers need this and both learned it the hard way: `worldFromRecording`
 * (a take carries a whole `PhysicsConfig`, so one recorded before a dial existed
 * has no entry for it) and `loadPlaytest` (the editor and the game ship as
 * separate builds and can be a version apart).
 *
 * **One level deeper than a spread**, which is the fix as of 2026-08-12.
 * `{ ...DEFAULT_PHYSICS, ...theirs }` fills a missing *group* and silently
 * replaces a present one wholesale -- so an old take carrying
 * `room: { decayRate: 0.3, decayTickMs: 3000 }` overwrote the whole `room` group
 * and left `decayPerTick` undefined. Nothing complained; the arithmetic simply
 * became `NaN` and every object's Energy read as null two systems later. Merging
 * per field means a renamed or added dial defaults while every value the caller
 * actually set stays authoritative.
 *
 * Deliberately does **not** try to migrate a renamed field to its new name. A
 * value recorded under `decayRate: 0.3` meant "30% of the remaining gap", and
 * there is no honest translation of that into "units per tick" -- the shapes are
 * different, not the spelling. So an old take replays under today's rules and
 * `diffDigests` reports exactly what came out different, which is the correct
 * answer for a *regression test of the rules* when the rules changed on purpose. */
export function withPhysicsDefaults(theirs: Partial<PhysicsConfig> | undefined): PhysicsConfig {
  const merged = clonePhysicsConfig();
  for (const [group, values] of Object.entries(theirs ?? {})) {
    if (!values || typeof values !== 'object') continue;
    const target = (merged as unknown as Record<string, Record<string, unknown>>)[group];
    if (!target) continue;
    for (const [key, value] of Object.entries(values as Record<string, unknown>)) {
      // Nested one more level for the grouped tables -- `fluid.water.*`,
      // `mob.grunt.*`, and every Precise/Standard/Unbound ladder.
      if (value && typeof value === 'object' && target[key] && typeof target[key] === 'object') {
        Object.assign(target[key] as object, value);
      } else if (key in target) {
        target[key] = value;
      }
    }
  }
  return merged;
}

/** A mutable copy, for a caller that wants to tune. `createWorldState(config)`
 * takes the result; `cloneWorldState` copies whatever a world already holds, so
 * forking a world and giving the fork different physics is the natural way to
 * ask what a number change would do. */
export function clonePhysicsConfig(config: PhysicsConfig = DEFAULT_PHYSICS): PhysicsConfig {
  return structuredClone(config);
}

function deepFreeze<T>(value: T): T {
  for (const key of Object.keys(value as object)) {
    const child = (value as Record<string, unknown>)[key];
    if (child && typeof child === 'object') deepFreeze(child);
  }
  return Object.freeze(value);
}
